import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  ChevronDownIcon,
  XMarkIcon,
  DocumentTextIcon,
  UserGroupIcon,
  BuildingOfficeIcon,
  CloudIcon,
  TrashIcon,
  CalendarDaysIcon
} from '@heroicons/react/24/outline';
import Layout from '../components/layout/Layout';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/common/LoadingSpinner';
import toast from 'react-hot-toast';
import api from '../services/api';

const OrphanedDataManager = () => {
  const [loading, setLoading] = useState(false);
  const [orphanedData, setOrphanedData] = useState({});
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [totalOrphanedCollections, setTotalOrphanedCollections] = useState(0);
  const [totalOrphanedRecords, setTotalOrphanedRecords] = useState(0);
  const [deletingRecord, setDeletingRecord] = useState(null);
  const [deletingCollection, setDeletingCollection] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    fetchOrphanedData();
  }, []);

  const fetchOrphanedData = async (companyId = '') => {
    try {
      setLoading(true);
      const params = companyId ? { companyId } : {};
      const response = await api.get('/companies/orphaned-data', { params });

      setOrphanedData(response.data.orphanedData);
      setCompanies(response.data.companies);
      setTotalOrphanedCollections(response.data.totalOrphanedCollections);
      setTotalOrphanedRecords(response.data.totalOrphanedRecords);
    } catch (error) {
      console.error('Error fetching orphaned data:', error);
      toast.error('Failed to fetch orphaned data');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRecord = async (collectionName, recordId) => {
    if (!window.confirm('Are you sure you want to delete this orphaned record? This action cannot be undone.')) {
      return;
    }

    try {
      setDeletingRecord(recordId);
      await api.delete(`/companies/orphaned-data/${collectionName}/${recordId}`);
      
      // Update local state
      setOrphanedData(prev => {
        const updated = { ...prev };
        if (updated[collectionName]) {
          updated[collectionName].count -= 1;
          updated[collectionName].records = updated[collectionName].records.filter(r => r._id !== recordId);
        }
        return updated;
      });
      
      setTotalOrphanedRecords(prev => prev - 1);
      toast.success('Orphaned record deleted successfully');
    } catch (error) {
      console.error('Error deleting record:', error);
      toast.error('Failed to delete record');
    } finally {
      setDeletingRecord(null);
    }
  };

  const handleDeleteCollection = async (collectionName) => {
    const collectionData = orphanedData[collectionName];
    if (!collectionData) return;

    if (!window.confirm(`Are you sure you want to delete all ${collectionData.count} orphaned records in ${getCollectionDisplayName(collectionName)}? This action cannot be undone.`)) {
      return;
    }

    try {
      setDeletingCollection(collectionName);
      await api.delete(`/companies/orphaned-data/${collectionName}`);
      
      // Update local state
      setOrphanedData(prev => {
        const updated = { ...prev };
        delete updated[collectionName];
        return updated;
      });
      
      setTotalOrphanedCollections(prev => prev - 1);
      setTotalOrphanedRecords(prev => prev - collectionData.count);
      toast.success(`All orphaned records in ${getCollectionDisplayName(collectionName)} deleted successfully`);
    } catch (error) {
      console.error('Error deleting collection:', error);
      toast.error('Failed to delete collection records');
    } finally {
      setDeletingCollection(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleCompanyFilter = (companyId) => {
    setSelectedCompany(companyId);
    fetchOrphanedData(companyId);
  };

  const getCollectionIcon = (collectionName) => {
    switch (collectionName) {
      case 'users':
        return <UserGroupIcon className="h-5 w-5" />;
      case 'leads':
      case 'customers':
        return <BuildingOfficeIcon className="h-5 w-5" />;
      case 'files':
      case 'gridfs_files':
        return <CloudIcon className="h-5 w-5" />;
      default:
        return <DocumentTextIcon className="h-5 w-5" />;
    }
  };

  const getRecordDisplayName = (record, collectionName) => {
    switch (collectionName) {
      case 'users':
        return record.name || record.email || 'Unnamed User';
      case 'leads':
        return record.name || record.email || 'Unnamed Lead';
      case 'customers':
        return record.name || record.email || 'Unnamed Customer';
      case 'followups':
        return record.title || `Follow-up for ${record.lead || 'Unknown Lead'}`;
      case 'files':
        return record.filename || record.originalname || 'Unnamed File';
      case 'lead_stages':
        return record.name || record.stage || 'Unnamed Stage';
      case 'performance_goals':
        return record.title || record.name || 'Unnamed Goal';
      case 'payments':
        return `Payment - ${record.amount ? `$${record.amount}` : 'Unknown Amount'}`;
      case 'company_usage':
        return `Usage for ${record.period || 'Unknown Period'}`;
      case 'gridfs_files':
        return record.filename || 'Unnamed GridFS File';
      default:
        return 'Unnamed Record';
    }
  };

  const getCollectionDisplayName = (collectionName) => {
    const names = {
      users: 'Users',
      leads: 'Leads',
      customers: 'Customers',
      followups: 'Follow-ups',
      files: 'Files',
      lead_stages: 'Lead Stages',
      performance_goals: 'Performance Goals',
      payments: 'Payments',
      company_usage: 'Company Usage',
      gridfs_files: 'GridFS Files'
    };
    return names[collectionName] || collectionName;
  };

  if (user?.role !== 'super_admin') {
    return (
      <Layout>
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center">
          <div className="text-center">
            <ExclamationTriangleIcon className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
            <p className="text-gray-600">You don't have permission to access this page.</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
        <div className="px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <div className="mb-4 sm:mb-0">
                <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
                  Orphaned Data Manager
                </h1>
                <p className="mt-2 text-gray-600 text-lg">
                  Find and manage data referencing deleted companies
                </p>
              </div>
              <div className="flex items-center space-x-4">
                {/* Company Filter */}
                <div className="relative">
                  <select
                    value={selectedCompany}
                    onChange={(e) => handleCompanyFilter(e.target.value)}
                    className="appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2 pr-8 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  >
                    <option value="">All Companies</option>
                    {companies.map((company) => (
                      <option key={company._id} value={company._id}>
                        {company.name}
                      </option>
                    ))}
                  </select>
                  <ChevronDownIcon className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                </div>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => fetchOrphanedData(selectedCompany)}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 flex items-center space-x-2"
                >
                  <MagnifyingGlassIcon className="h-4 w-4" />
                  <span>Refresh</span>
                </motion.button>
              </div>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Orphaned Collections</p>
                  <p className="text-3xl font-bold text-gray-900">{totalOrphanedCollections}</p>
                </div>
                <DocumentTextIcon className="h-8 w-8 text-red-500" />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Orphaned Records</p>
                  <p className="text-3xl font-bold text-gray-900">{totalOrphanedRecords.toLocaleString()}</p>
                </div>
                <ExclamationTriangleIcon className="h-8 w-8 text-orange-500" />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Companies</p>
                  <p className="text-3xl font-bold text-gray-900">{companies.length}</p>
                </div>
                <BuildingOfficeIcon className="h-8 w-8 text-blue-500" />
              </div>
            </motion.div>
          </div>

          {/* Orphaned Data List */}
          {loading ? (
            <div className="flex justify-center py-12">
              <LoadingSpinner />
            </div>
          ) : Object.keys(orphanedData).length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="bg-white rounded-2xl shadow-xl border border-gray-100 p-12 text-center"
            >
              <div className="text-green-500 mb-4">
                <svg className="h-16 w-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No Orphaned Data Found</h3>
              <p className="text-gray-600">All company data is properly linked. Great job maintaining data integrity!</p>
            </motion.div>
          ) : (
            <div className="space-y-6">
              {Object.entries(orphanedData).map(([collectionName, data], index) => (
                <motion.div
                  key={collectionName}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden"
                >
                  <div className="p-6 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-red-100 rounded-lg">
                          {getCollectionIcon(collectionName)}
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">
                            {getCollectionDisplayName(collectionName)}
                          </h3>
                          <p className="text-sm text-gray-600">
                            {data.count.toLocaleString()} orphaned records
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-4">
                        <div className="text-right">
                          <div className="text-2xl font-bold text-red-600">{data.count}</div>
                          <div className="text-sm text-gray-500">of {data.totalRecords.toLocaleString()} total</div>
                        </div>
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => handleDeleteCollection(collectionName)}
                          disabled={deletingCollection === collectionName}
                          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {deletingCollection === collectionName ? (
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          ) : (
                            <TrashIcon className="h-4 w-4" />
                          )}
                          <span>Delete All</span>
                        </motion.button>
                      </div>
                    </div>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            ID
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Name
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Created Date
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {data.records.map((record) => (
                          <motion.tr
                            key={record._id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            className="hover:bg-gray-50"
                          >
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                              {record._id}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {getRecordDisplayName(record, collectionName)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              <div className="flex items-center space-x-2">
                                <CalendarDaysIcon className="h-4 w-4 text-gray-400" />
                                <span>{formatDate(record.createdAt)}</span>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={() => handleDeleteRecord(collectionName, record._id)}
                                disabled={deletingRecord === record._id}
                                className="px-3 py-1 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 flex items-center space-x-1 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {deletingRecord === record._id ? (
                                  <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                ) : (
                                  <TrashIcon className="h-3 w-3" />
                                )}
                                <span>Delete</span>
                              </motion.button>
                            </td>
                          </motion.tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default OrphanedDataManager;